import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type RefObject,
  type ReactNode,
  type UIEvent,
} from "react";
const ChevronDown = ({ size = 15 }: { size?: number; "aria-hidden"?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m6 9 6 6 6-6" />
  </svg>
);
const Search = ({ size = 14 }: { size?: number; "aria-hidden"?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.3-4.3" />
  </svg>
);

export interface SelectOption {
  value: string;
  label: string;
  /** Secondary text shown under the label (for example an SKU). */
  detail?: string;
  disabled?: boolean;
}
export interface SelectPage {
  items: SelectOption[];
  /** Total matches when known; otherwise `hasMore` drives further loading. */
  total?: number;
  hasMore?: boolean;
}
/** Loads one page of options for a query. Must honour `signal`. */
export type SelectLoader = (
  query: string,
  offset: number,
  limit: number,
  signal: AbortSignal,
) => Promise<SelectPage>;

/** Options at or above this count get a search box inside the dropdown. */
export const searchThreshold = 5;
/** Remote options are fetched in batches of this size. */
export const searchPageSize = 10;
const rowHeight = 36;
const visibleRows = 8;

type Props = {
  value: string;
  onChange(value: string, option?: SelectOption): void;
  /** Accessible name; omit when a wrapping <label> names the control. */
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  /** A local option list, searched in the browser. */
  options?: SelectOption[];
  /** Or a remote loader that searches the full data set page by page. */
  load?: SelectLoader;
  /** Label for the current value when it is not among loaded options. */
  selectedLabel?: string;
  /** Text for an explicit empty choice, such as "Choose…"; omit to hide it. */
  emptyOption?: string;
  searchPlaceholder?: string;
  className?: string;
  renderOption?: (option: SelectOption) => ReactNode;
  /**
   * Native <option> children. When given, the trigger is a real <select>
   * holding them (so labels, form values and automation keep working); its
   * own list never opens and the searchable popover is used instead.
   */
  nativeOptions?: ReactNode;
  /** Extra attributes for the native trigger (name, aria-*, required…). */
  triggerProps?: Record<string, unknown>;
};

/**
 * One dropdown behaviour for the whole application: search lives inside the
 * opened list and receives focus immediately (unless there are fewer than five
 * options), remote data is searched in full and loaded in batches while
 * scrolling, and stale responses never replace the current results.
 */
export function SearchableSelect({
  value,
  onChange,
  label,
  placeholder = "Choose…",
  disabled,
  id,
  options,
  load,
  selectedLabel,
  emptyOption,
  searchPlaceholder = "Search…",
  className = "",
  renderOption,
  nativeOptions,
  triggerProps,
}: Props) {
  const generatedId = useId();
  const triggerId = id || generatedId,
    listId = `${triggerId}-list`;
  const [open, setOpen] = useState(false),
    [query, setQuery] = useState(""),
    [active, setActive] = useState(0),
    [scrollTop, setScrollTop] = useState(0);
  const [remote, setRemote] = useState<{
    query: string;
    items: SelectOption[];
    total?: number;
    hasMore: boolean;
    loading: boolean;
    error?: string;
  }>({ query: "", items: [], hasMore: true, loading: false });
  const known = useRef(new Map<string, SelectOption>());
  const request = useRef(0),
    controller = useRef<AbortController>(undefined);
  const root = useRef<HTMLDivElement>(null),
    trigger = useRef<HTMLElement>(null),
    search = useRef<HTMLInputElement>(null),
    list = useRef<HTMLDivElement>(null),
    popover = useRef<HTMLDivElement>(null);
  // The open list is positioned against the viewport so scrolling panels and
  // overflow-clipped containers never cut it off; it flips above the trigger
  // when there is more room there.
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const anchor = root.current,
        panel = popover.current;
      if (!anchor || !panel) return;
      const rect = anchor.getBoundingClientRect(),
        height = panel.offsetHeight,
        view = anchor.ownerDocument.defaultView || window,
        below = view.innerHeight - rect.bottom,
        up = below < height + 8 && rect.top > below;
      const top = up ? Math.max(4, rect.top - height - 4) : rect.bottom + 4;
      Object.assign(panel.style, {
        position: "fixed",
        top: `${top}px`,
        left: `${rect.left}px`,
        right: "auto",
        width: `${rect.width}px`,
      });
      // A transformed ancestor becomes the containing block; correct for it.
      const actual = panel.getBoundingClientRect();
      if (Math.abs(actual.top - top) > 0.5 || Math.abs(actual.left - rect.left) > 0.5)
        Object.assign(panel.style, {
          top: `${top - (actual.top - top)}px`,
          left: `${rect.left - (actual.left - rect.left)}px`,
        });
      panel.dataset.placement = up ? "top" : "bottom";
    };
    place();
    const view = root.current?.ownerDocument.defaultView || window;
    view.addEventListener("scroll", place, true);
    view.addEventListener("resize", place);
    const observer =
      typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(place);
    if (popover.current) observer?.observe(popover.current);
    return () => {
      view.removeEventListener("scroll", place, true);
      view.removeEventListener("resize", place);
      observer?.disconnect();
    };
  }, [open]);

  const fetchPage = useCallback(
    (nextQuery: string, offset: number) => {
      if (!load) return;
      controller.current?.abort();
      const abort = new AbortController(),
        id = ++request.current;
      controller.current = abort;
      setRemote((current) => ({
        ...(offset ? current : { items: [], hasMore: true }),
        query: nextQuery,
        loading: true,
        error: undefined,
      }));
      load(nextQuery, offset, searchPageSize, abort.signal).then(
        (page) => {
          // A newer query or page superseded this response.
          if (id !== request.current) return;
          for (const item of page.items) known.current.set(item.value, item);
          setRemote((current) => {
            const seen = new Set(current.items.map((item) => item.value));
            const items = offset
              ? [
                  ...current.items,
                  ...page.items.filter((item) => !seen.has(item.value)),
                ]
              : page.items;
            return {
              query: nextQuery,
              items,
              total: page.total,
              hasMore:
                page.hasMore ??
                (page.total !== undefined
                  ? items.length < page.total
                  : page.items.length >= searchPageSize),
              loading: false,
            };
          });
        },
        (error: unknown) => {
          if (id !== request.current || abort.signal.aborted) return;
          setRemote((current) => ({
            ...current,
            loading: false,
            error:
              error instanceof Error && error.message
                ? error.message
                : "Options could not be loaded.",
          }));
        },
      );
    },
    [load],
  );
  useEffect(() => () => controller.current?.abort(), []);

  // Local filtering, or the remote pages for the current query.
  const filtered = useMemo(() => {
    if (load) return remote.items;
    const needle = query.trim().toLocaleLowerCase();
    return (options || []).filter(
      (option) =>
        !needle ||
        option.label.toLocaleLowerCase().includes(needle) ||
        option.detail?.toLocaleLowerCase().includes(needle) ||
        option.value.toLocaleLowerCase().includes(needle),
    );
  }, [load, remote.items, options, query]);
  const rows: SelectOption[] = useMemo(
    () => [
      ...(emptyOption && !query.trim()
        ? [{ value: "", label: emptyOption }]
        : []),
      ...filtered,
    ],
    [emptyOption, query, filtered],
  );
  // Under five options there is nothing to search: a remote list that fits
  // in one short, complete first page drops its search box too.
  const searchable = load
    ? !!query ||
      remote.loading ||
      remote.query !== "" ||
      remote.hasMore ||
      remote.items.length >= searchThreshold
    : (options?.length || 0) >= searchThreshold;

  // Remote: reload from the first page whenever the query changes.
  useEffect(() => {
    if (!open || !load) return;
    const timer = setTimeout(() => fetchPage(query, 0), query ? 200 : 0);
    return () => clearTimeout(timer);
  }, [open, load, query, fetchPage]);

  const current =
    (options || []).find((option) => option.value === value) ||
    known.current.get(value) ||
    remote.items.find((option) => option.value === value);
  if (current) known.current.set(current.value, current);
  const display =
    current?.label ||
    (value ? selectedLabel || value : emptyOption || placeholder);

  useLayoutEffect(() => {
    if (!open) return;
    if (searchable) search.current?.focus();
    else list.current?.focus();
  }, [open, searchable]);
  useEffect(() => {
    if (!open) return;
    const index = rows.findIndex((row) => row.value === value);
    setActive(index >= 0 ? index : 0);
    // Only when opening: keep the selection in view.
    if (index >= 0 && list.current)
      list.current.scrollTop = Math.max(
        0,
        index * rowHeight - rowHeight * 2,
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const pointer = (event: PointerEvent) => {
      if (!root.current?.contains(event.composedPath()[0] as Node))
        setOpen(false);
    };
    document.addEventListener("pointerdown", pointer, true);
    return () => document.removeEventListener("pointerdown", pointer, true);
  }, [open]);

  const close = (refocus = true) => {
    setOpen(false);
    setQuery("");
    setScrollTop(0);
    if (refocus) trigger.current?.focus();
  };
  const choose = (option: SelectOption) => {
    if (option.disabled) return;
    known.current.set(option.value, option);
    onChange(option.value, option);
    close();
  };
  const ensureVisible = (index: number) => {
    const element = list.current;
    if (!element) return;
    const top = index * rowHeight;
    if (top < element.scrollTop) element.scrollTop = top;
    else if (top + rowHeight > element.scrollTop + element.clientHeight)
      element.scrollTop = top + rowHeight - element.clientHeight;
  };
  const move = (index: number) => {
    const next = Math.max(0, Math.min(rows.length - 1, index));
    setActive(next);
    ensureVisible(next);
  };
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      move(active + 1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      move(active - 1);
    } else if (event.key === "Home" && !searchable) {
      event.preventDefault();
      move(0);
    } else if (event.key === "End" && !searchable) {
      event.preventDefault();
      move(rows.length - 1);
    } else if (event.key === "PageDown") {
      event.preventDefault();
      move(active + visibleRows);
    } else if (event.key === "PageUp") {
      event.preventDefault();
      move(active - visibleRows);
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (rows[active]) choose(rows[active]);
    } else if (event.key === "Escape") {
      event.preventDefault();
      close();
    } else if (event.key === "Tab") close(false);
  };
  // Virtual window over the rows.
  const start = Math.max(0, Math.floor(scrollTop / rowHeight) - 4),
    end = Math.min(rows.length, start + visibleRows + 8);
  const onScroll = (event: UIEvent<HTMLDivElement>) => {
    const element = event.currentTarget;
    setScrollTop(element.scrollTop);
    if (
      load &&
      remote.hasMore &&
      !remote.loading &&
      !remote.error &&
      element.scrollTop + element.clientHeight >=
        element.scrollHeight - rowHeight * 3
    )
      fetchPage(query, remote.items.length);
  };
  // Remote: fill the visible list when a page is shorter than the viewport.
  useEffect(() => {
    if (
      open &&
      load &&
      remote.hasMore &&
      !remote.loading &&
      !remote.error &&
      remote.items.length &&
      remote.query === query &&
      remote.items.length * rowHeight < visibleRows * rowHeight
    )
      fetchPage(query, remote.items.length);
  }, [open, load, remote, query, fetchPage]);

  const status = remote.error
    ? remote.error
    : remote.loading && !rows.length
      ? "Loading…"
      : !rows.length
        ? query
          ? "No matches"
          : "No options"
        : "";
  const optionId = (index: number) => `${triggerId}-option-${index}`;
  return (
    <div
      ref={root}
      className={`searchable-select ${open ? "open" : ""} ${className}`}
    >
      {nativeOptions !== undefined ? (
        <select
          {...triggerProps}
          ref={trigger as RefObject<HTMLSelectElement>}
          id={triggerId}
          className="searchable-select-trigger native"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-label={label || undefined}
          disabled={disabled}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onMouseDown={(event) => {
            // Keep the native list closed; the searchable popover opens instead.
            event.preventDefault();
            if (disabled) return;
            trigger.current?.focus();
            if (open) close();
            else setOpen(true);
          }}
          onKeyDown={(event) => {
            if (
              ["ArrowDown", "ArrowUp", "Enter", " ", "F4"].includes(
                event.key,
              ) ||
              (event.altKey && event.key.startsWith("Arrow"))
            ) {
              event.preventDefault();
              setOpen(true);
            }
          }}
        >
          {nativeOptions}
        </select>
      ) : (
        <button
          ref={trigger as RefObject<HTMLButtonElement>}
          id={triggerId}
          type="button"
          className="searchable-select-trigger"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-label={label || undefined}
          disabled={disabled}
          onClick={() => (open ? close() : setOpen(true))}
          onKeyDown={(event) => {
            if (["ArrowDown", "ArrowUp", "Enter", " "].includes(event.key)) {
              event.preventDefault();
              setOpen(true);
            }
          }}
        >
          <span className={value ? "" : "placeholder"}>{display}</span>
          <ChevronDown size={15} aria-hidden="true" />
        </button>
      )}
      {open && (
        <div ref={popover} className="searchable-select-popover">
          {searchable && (
            <div className="searchable-select-search">
              <Search size={14} aria-hidden="true" />
              <input
                ref={search}
                type="search"
                role="combobox"
                aria-label={label ? `Search ${label}` : "Search options"}
                aria-expanded="true"
                aria-controls={listId}
                aria-autocomplete="list"
                aria-activedescendant={
                  rows[active] ? optionId(active) : undefined
                }
                placeholder={searchPlaceholder}
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setActive(0);
                  setScrollTop(0);
                  if (list.current) list.current.scrollTop = 0;
                }}
                onKeyDown={onKeyDown}
              />
            </div>
          )}
          <div
            ref={list}
            id={listId}
            role="listbox"
            aria-label={label || "Options"}
            tabIndex={searchable ? -1 : 0}
            aria-activedescendant={
              !searchable && rows[active] ? optionId(active) : undefined
            }
            aria-busy={remote.loading}
            className="searchable-select-list"
            style={{
              maxHeight: rowHeight * visibleRows,
            }}
            onScroll={onScroll}
            onKeyDown={searchable ? undefined : onKeyDown}
          >
            <div style={{ height: rows.length * rowHeight, position: "relative" }}>
              {rows.slice(start, end).map((option, offset) => {
                const index = start + offset;
                return (
                  <div
                    key={`${option.value}:${index}`}
                    id={optionId(index)}
                    role="option"
                    data-value={option.value}
                    aria-selected={option.value === value}
                    aria-disabled={option.disabled || undefined}
                    className={`searchable-select-option ${
                      index === active ? "active" : ""
                    }`}
                    style={{
                      position: "absolute",
                      top: index * rowHeight,
                      left: 0,
                      right: 0,
                      height: rowHeight,
                    }}
                    onPointerMove={() => setActive(index)}
                    onPointerDown={(event) => event.preventDefault()}
                    onClick={() => choose(option)}
                  >
                    {renderOption ? (
                      renderOption(option)
                    ) : (
                      <>
                        <span>{option.label}</span>
                        {option.detail && <small>{option.detail}</small>}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          {(status || (remote.loading && rows.length > 0)) && (
            <p
              className={`searchable-select-status ${remote.error ? "error" : ""}`}
              role={remote.error ? "alert" : "status"}
            >
              {status || "Loading more…"}
              {remote.error && (
                <button
                  type="button"
                  className="text-button"
                  onClick={() => fetchPage(query, remote.items.length)}
                >
                  Retry
                </button>
              )}
            </p>
          )}
          {load && remote.total !== undefined && rows.length > 0 && (
            <p className="searchable-select-count" aria-live="polite">
              {remote.items.length} of {remote.total}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
